<template>
  <v-container class="pa-0 mb-8 payment-container">
    <NavHeader
      :title="$t('screens.payment.headerTitle')"
      :subtitle="navHeaderSubtitle"
      @onBackClick="goBack"
    />
    <LayoutCard v-if="error" rounded>
      <StateScreen
        :title="errorResponseTitle"
        :subtitle="errorResponseDesc"
        :image="require(`@/assets/images/order-failed-image.svg`)"
      />
    </LayoutCard>
    <div v-if="isInitialLoad && !error" class="d-flex justify-center pt-8">
      <v-progress-circular indeterminate color="black" />
    </div>
    <div v-show="!isInitialLoad && !error">
      <LayoutCard :title="$t('screens.payment.summaryTitle')" rounded>
        <PriceSummary
          :subtotal-amount="subtotal"
          :total-amount="totalAmount"
          :transaction-fee="transactionFee"
          :discount-amount="discountAmount"
          :coupon-code="couponCode"
          :small-order-fee="smallOrderFee"
        />
      </LayoutCard>
      <form id="payment-form">
        <LayoutCard rounded :title="$t('screens.payment.paymentMethods')">
          <PaymentMethods
            ref="paymentMethods"
            :is-loading-pr-button="isLoading"
            :preselected-payment-method="presentPaymentMethod"
            :payment-methods-override="paymentMethodsOverride"
            @on-change="onPaymentMethodChange"
            @on-success="goToSuccess"
            @on-error="onPaymentError"
          />
        </LayoutCard>
        <div>
          <v-alert
            v-if="errorMessage"
            border="left"
            colored-border
            color="red accent-4"
            class="mt-6"
            dismissible
            close-icon="mdi-close"
            >{{ errorMessage }}</v-alert
          >
          <div id="payment-message" class="hidden"></div>
          <v-container class="px-10">
            <div v-show="showPayCta">
              <Button
                ref="CTAButton"
                dark
                :loading="isLoading"
                :disabled="ctaDisabled"
                data-cy="pay-cta-food-court"
                @click="onPayCtaPress"
              >
                <div class="menu-item-sheet-controls__add-btn">
                  <div>
                    {{ payCtaLabel }}
                  </div>
                  <div>
                    {{ totalAmountFormatted }}
                  </div>
                </div>
              </Button>
            </div>
            <TermsAndConditions />
          </v-container>
        </div>
      </form>
      <PaymentStatusBackdrop ref="backdrop" />
    </div>
    <PaymentBottomSheetError
      ref="paymentBottomSheetError"
      @paymentErrorBottomSheetCtaPress="generateSolidPaymentIntent"
    />
  </v-container>
</template>

<script>
import NavHeader from '@/components/Navigation/NavHeader.vue';
import LayoutCard from '@/components/LayoutCard.vue';
import Button from '@/components/Button.vue';
import TermsAndConditions from '@/components/common/TermsAndConditions.vue';
import PaymentMethods from '@/components/Payments/PaymentMethods.vue';
import PriceSummary from '@/components/common/PriceSummary.vue';
import StateScreen from '@/components/StateScreen.vue';
import PaymentBottomSheetError from '@/components/Payments/PaymentBottomSheetError.vue';
import PaymentStatusBackdrop from '@/components/Payments/PaymentStatusBackdrop.vue';

import { PAYMENT_METHODS } from '@/utils/constants';

export default {
  components: {
    LayoutCard,
    Button,
    NavHeader,
    TermsAndConditions,
    PaymentMethods,
    PriceSummary,
    StateScreen,
    PaymentBottomSheetError,
    PaymentStatusBackdrop,
  },
  data() {
    return {
      subtotal: null,
      totalAmount: null,
      transactionFee: null,
      discountAmount: null,
      couponCode: null,
      smallOrderFee: null,
      isLoading: false,
      isInitialLoad: true,
      error: null,
      errorMessage: null,
      uniqueOrderNumber: null,
      neopayPaymentLink: null,
      paymentMethod: null,
      neopayPaymentData: null,
      errorResponseTitle: null,
      errorResponseDesc: null,
      expiresAt: null,
      solidGatePaymentUuid: null,
      periodicCheckTimer: null,
    };
  },
  computed: {
    paymentMethodsOverride() {
      // CHECK WITH FOOD COURT PAYMENT PROVIDERS
      const providers = this.$store.getters.getFoodCourtPaymentProviders;

      return providers.flatMap(provider => {
        switch (provider.provider) {
          case 'solidgate':
            return [PAYMENT_METHODS.SOLID_WALLET, PAYMENT_METHODS.SOLID_CARD];
          case 'neopay':
            return [PAYMENT_METHODS.NEOPAY];
          default:
            return [];
        }
      });
    },
    showPayCta() {
      return (
        this.paymentMethod === PAYMENT_METHODS.NEOPAY ||
        this.paymentMethod === PAYMENT_METHODS.SOLID_CARD
      );
    },
    payCtaLabel() {
      return this.$t('screens.payment.payCtaLabel');
    },
    ctaDisabled() {
      if (
        [PAYMENT_METHODS.NEOPAY, PAYMENT_METHODS.SOLID_CARD].includes(
          this.paymentMethod
        )
      ) {
        return false;
      }

      return true;
    },
    tableName() {
      return this.$store.state.table.name;
    },
    returnUrl() {
      const orderRedirectUrl = `${process.env.VUE_APP_BASE_URL}/menu/${this.$store.state.session}?order=${this.uniqueOrderNumber}`;
      return orderRedirectUrl;
    },
    totalAmountFormatted() {
      return this.$store.getters.getFormattedPrice(this.totalAmount);
    },
    orderAlreadyPaid() {
      return this.$store.getters.getIsOrderAlreadyPaid(
        this.$route.params.order
      );
    },
    solidEnabled() {
      return this.$store.getters.paymentProviderSolidEnabled;
    },
    presentPaymentMethod() {
      if (this.solidEnabled) {
        return PAYMENT_METHODS.SOLID_WALLET;
      }
      return '';
    },
    foodCourtHash() {
      return this.$route.params.foodCourtHash;
    },
    foodCourtSession() {
      return (
        this.$store.getters.getFoodCourtSession || this.params.foodCourtSession
      );
    },
    placedOrders() {
      return this.$store.getters.getPlaceOrdersIds;
    },
    placedOrderIds() {
      if (this.placedOrders.length) {
        return this.placedOrders;
      }

      return this.$route.params.placedOrdersIds
        ? this.$route.params.placedOrdersIds.split(',')
        : [];
    },
    foodCourtCarts() {
      return this.$store.getters.getFoodCourtCarts || [];
    },
    hasOrderFromMultipleRestaurants() {
      return this.foodCourtCarts.length > 1;
    },
    navHeaderSubtitle() {
      if (this.hasOrderFromMultipleRestaurants) {
        return this.$t('screens.foodCourtCart.headerSubtitlePlural');
      }

      return '';
    },
  },
  watch: {
    async orderAlreadyPaid(isPaid) {
      if (isPaid) {
        await this.navigateToOrder();
      }
    },
  },
  created() {
    document.addEventListener(
      'visibilitychange',
      this.checkIfPaymentSessionExpired
    );
  },
  beforeDestroy() {
    document.removeEventListener(
      'visibilitychange',
      this.checkIfPaymentSessionExpired
    );
    clearInterval(this.periodicCheckTimer);
  },
  async mounted() {
    this.isInitialLoad = true;

    window.scrollTo({ top: 0 });

    // Load food court details and carts
    await this.$store.dispatch('fetchFoodCourtDetails', {
      hash: this.foodCourtHash,
    });
    await this.$store.dispatch('fetchFoodCourtCarts', {
      foodCourtSession: this.foodCourtSession,
    });
    await this.checkIfPaymentSessionExpired();
    await this.handlePaymentIntents();

    this.isInitialLoad = false;

    this.periodicCheckTimer = setInterval(() => {
      this.checkIfPaymentSessionExpired();
    }, 10000);
  },
  methods: {
    navigateToNeopay() {
      if (this.neopayPaymentLink) {
        this.isLoading = true;
        window.location = this.neopayPaymentLink;
      }
    },
    async generateSolidPaymentIntent() {
      const dispatch = this.$store.dispatch;
      const solidEnabled = this.paymentMethodsOverride.includes(
        PAYMENT_METHODS.SOLID_WALLET
      );

      if (!solidEnabled) {
        return;
      }

      await dispatch('generateCompositeSolidGatePaymentIntent', {
        foodCourtSession: this.foodCourtSession,
        orders: this.placedOrderIds,
      });

      const intentData = this.$store.getters.solidGatePaymentIntent;

      if (!intentData) {
        return;
      }

      this.expiresAt = intentData.expires_at;
      this.subtotal = intentData.subtotal;
      this.totalAmount = intentData.total;
      this.transactionFee = intentData.transaction_fee;
      this.discountAmount = intentData.discount;
      this.couponCode = intentData.coupon;
      this.uniqueOrderNumber = intentData.unique_order_number;
      this.smallOrderFee = intentData?.small_order_fee || 0;
      this.solidGatePaymentUuid = intentData.uuid;
    },
    async createNeopayTransaction() {
      const neopayEnabled = this.paymentMethodsOverride.includes(
        PAYMENT_METHODS.NEOPAY
      );

      if (!neopayEnabled) {
        return;
      }

      try {
        const paymentRequest = await this.$store.dispatch(
          'generateCompositeNeonPayTransaction',
          {
            foodCourtSession: this.foodCourtSession,
            orders: this.placedOrderIds,
          }
        );

        this.neopayPaymentData = paymentRequest;
        this.neopayPaymentLink = paymentRequest.redirect_url;
        return;
      } catch (error) {
        this.handleError(error);
      }
    },
    handleError(error) {
      if (error.response.data.message === 'Order is already paid') {
        this.error = true;
        return this.navigateToOrder();
      } else if (
        error.response.data.message ===
        'Order already has payments of other type'
      ) {
        this.error = true;
        this.errorResponseTitle = this.$t(
          'screens.payment.error.partAlreadyPaid.title'
        );
        this.errorResponseDesc = this.$t(
          'screens.payment.error.partAlreadyPaid.description'
        );
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    async showBackdrop(backdrop, timeout) {
      await this.$refs.backdrop.hide(); // hide previous backdrop
      await this.$refs.backdrop.show(backdrop, timeout);
    },
    hideBackdrop() {
      this.$refs.backdrop.hide();
    },
    async goToSuccess() {
      await this.showBackdrop('success', 2000);

      this.sendPurchaseAnalytics('wallet');

      this.navigateToOrder();
      return;
    },
    onPaymentError(error) {
      const errorCodeMap = {
        '3.02': 'insufficient_funds',
      };

      const adoptedError = {
        ...error,
        decline_code: errorCodeMap[error.code] || 'generic',
      };

      this.hideBackdrop();
      this?.$refs?.paymentBottomSheetError?.open(adoptedError);
    },
    async navigateToOrder() {
      this.$router.replace({
        name: 'PaymentStatus',
        params: {
          session: '1', // Hardcoded session for food court
        },
        query: {
          origin: 'food-court',
          paymentUuid: this.solidGatePaymentUuid,
          foodCourtHash: this.foodCourtHash,
        },
      });
      this.hideBackdrop();
    },
    onPaymentMethodChange(method) {
      this.paymentMethod = method;

      if (method === PAYMENT_METHODS.NEOPAY) {
        setTimeout(this.scrollToPayCTA, 300);
        this.subtotal = this.neopayPaymentData.subtotal;
        this.totalAmount = this.neopayPaymentData.total;
        this.transactionFee = this.neopayPaymentData.transaction_fee;
        this.smallOrderFee = this.neopayPaymentData?.small_order_fee || 0;
      } else if (
        method === PAYMENT_METHODS.SOLID_CARD ||
        method === PAYMENT_METHODS.SOLID_WALLET
      ) {
        const solidGatePaymentIntent = this.$store.getters
          .solidGatePaymentIntent;
        this.subtotal = solidGatePaymentIntent.subtotal;
        this.totalAmount = solidGatePaymentIntent.total;
        this.transactionFee = solidGatePaymentIntent.transaction_fee;
        this.smallOrderFee = solidGatePaymentIntent?.small_order_fee || 0;
      }
    },
    async onPayCtaPress(e) {
      e.preventDefault();
      if (this.paymentMethod === PAYMENT_METHODS.NEOPAY) {
        this.sendPurchaseAnalytics(PAYMENT_METHODS.NEOPAY);
        this.navigateToNeopay();
      } else if (this.paymentMethod === PAYMENT_METHODS.SOLID_CARD) {
        // Sketchy way to submit the form
        this.$refs.paymentMethods.$refs?.solidCardForm?.submit();
      }
    },
    sendPurchaseAnalytics(method) {
      this.$store.dispatch('sendAnalyticsEvent', {
        event: 'purchase',
        transaction_id: this?.uniqueOrderNumber,
        value: this?.totalAmount,
        currency: this.$store.getters.getCurrencyCode,
        method,
      });
    },
    async scrollToPayCTA() {
      await new Promise(resolve => setTimeout(resolve, 100));
      this.$refs.CTAButton.$el.scrollIntoView({ behavior: 'smooth' });
    },
    async checkIfPaymentSessionExpired() {
      const hidden = document.hidden;
      const notPaymentScreen = this.$route.name !== 'FoodCourtPayment';
      const loading = this.isLoading;
      const backdropVisible = this.$refs.backdrop?.backdropVisible || false;

      if (hidden || notPaymentScreen || loading || backdropVisible) {
        return;
      }

      const onExpired = () => {
        if (hidden || notPaymentScreen || loading || backdropVisible) {
          return;
        }

        this.goBack();
        this.$store.dispatch('showToastInstantly', {
          title: this.$t(
            'screens.payment.error.paymentUnavailableGeneric.title'
          ),
          description: this.$t('screens.payment.paymentSessionExpired'),
        });
      };
      try {
        if (this.solidGatePaymentUuid) {
          await this.$store.dispatch('checkIfPaymentIntentExpired', {
            paymentUuid: this.solidGatePaymentUuid,
            onExpired,
          });
        }
        if (this.neopayPaymentData?.uuid) {
          await this.$store.dispatch('checkIfPaymentIntentExpired', {
            paymentUuid: this.neopayPaymentData?.uuid,
            onExpired,
          });
        }
      } catch (error) {
        console.log(error);
        onExpired();
      }
    },
    async handlePaymentIntents() {
      await Promise.all([
        this.generateSolidPaymentIntent(),
        this.createNeopayTransaction(),
      ]);
    },
  },
};
</script>

<style lang="scss" scoped>
.payment-container {
  background-color: $color-background-gray;
  height: 100%;
}
</style>
